import type CycleTimeChartController from 'components/CycleTimeChart/controller/CycleTimeChartController';
import ApplicationModel from 'components/framework/ApplicationModel';
import type StatusRangeFilter from 'components/framework/filters/StatusRangeFilter';
import NumericFilter, {
  type NumericFilterConfig,
} from 'components/framework/filters/NumericFilter';
import Fragment from 'components/framework/Fragment';
import {
  type RecordEvent,
  type RecordEventRaw,
} from 'components/framework/models/generated/types';
import type WorkflowStatus from 'components/framework/models/WorkflowStatus';
import { orZero } from 'javascripts/util/orZero';
import { get } from 'lodash';

type LoadEventParameters = {
  startDate: string;
  endDate: string;
  projectId: RecordId;
  availableStatuses: WorkflowStatus[];
  customFieldKeys: string[];
  teamIds?: string[];
  controller: CycleTimeChartController;
};

type EventBatchParameters = {
  projectId: RecordId;
  startDate?: string;
  endDate: string;
  statusIds?: string[];
  customFieldKeys?: string[];
  teamIds?: string[];
  controller: CycleTimeChartController;
};

type FilterEventParams = {
  events: RecordEventRaw[];
  availableStatuses: WorkflowStatus[];
  statusRange: StatusRangeFilter;
  subjectType?: string;
  userId?: RecordId;
  featureType?: string;
  customFieldFilters?: Record<
    string,
    string | NumericFilterConfig | string[] | undefined
  >;
  teamIds?: string[];
  multilineReport?: boolean;
};

const EVENTS_PER_PAGE = 300;
const INITIAL_CONCURRENT_PAGES = 2;
const MAX_CONCURRENT_PAGES = 5;

// Loads all events for all statuses and filters client side.
// This is a slower initial load but faster when toggling filters.
export async function loadCycleTimeEvents({
  startDate,
  endDate,
  availableStatuses,
  customFieldKeys,
  projectId,
  teamIds,
  controller,
}: LoadEventParameters): Promise<RecordEventRaw[]> {
  return loadEventsInBatches({
    projectId,
    startDate,
    endDate,
    statusIds: availableStatuses.map(s => s.id),
    customFieldKeys,
    teamIds,
    controller,
  });
}

const isNumericFilter = (filter: any): filter is NumericFilterConfig => {
  return filter && typeof filter === 'object' && 'numeric_type' in filter;
};

export function filterEvents({
  events,
  statusRange,
  availableStatuses,
  subjectType,
  userId,
  featureType,
  customFieldFilters,
  teamIds,
  multilineReport,
}: FilterEventParams): RecordEventRaw[] {
  const startStatus: WorkflowStatus | undefined = availableStatuses.find(
    status => status.id === statusRange.start
  );

  const statusIdMap = availableStatuses.reduce((acc, status) => {
    acc.set(status.name, status.id);
    return acc;
  }, new Map());

  const filteredEvents: RecordEventRaw[] = events.filter(event => {
    if (subjectType && event.eventSubjectType !== subjectType) return false;

    if (userId && event.assignedToUserId !== userId) return false;

    if (multilineReport && teamIds?.length && !teamIds.includes(event.teamId)) {
      return false;
    }

    if (
      event.teamWorkflowStatus &&
      startStatus &&
      event.teamWorkflowStatus.position < startStatus.position
    )
      return false;

    if (featureType && event.eventSubjectType === 'Feature') {
      if (get(event, 'eventSubject.workflowKind.name') !== featureType) {
        return false;
      }
    }

    if (customFieldFilters && event.eventSubjectType === 'Feature') {
      const customFields: { key: string; value: string }[] = get(
        event,
        'eventSubject.customFieldValues',
        []
      );

      for (const [key, filterValue] of Object.entries(customFieldFilters)) {
        // If no filter set for this key, skip
        if (typeof filterValue === 'undefined') continue;

        const field = customFields.find(f => f.key === key);

        if (isNumericFilter(filterValue)) {
          const nf = new NumericFilter(filterValue);
          if (!nf.hasValue) continue;

          const numberValue =
            field && field.value !== '' ? Number(field.value) : undefined;
          if (!nf.matches(numberValue)) return false;
          continue;
        }

        if (Array.isArray(filterValue)) {
          // Array-based (multi-select) filters: require one-of match, ignore empty array
          if (filterValue.length === 0) continue;
          if (!field || !filterValue.includes(field.value)) return false;
          continue;
        }

        // String-based (select) filters: require exact match, ignore empty string
        if (filterValue === '') continue;
        if (!field || field.value !== filterValue) return false;
        continue;
      }
    }

    // TODO: For Nov 13 demo, better handling for non-equivalent statuses TBD
    if (
      multilineReport &&
      event.teamWorkflowStatus &&
      !statusIdMap.has(event.teamWorkflowStatus.name)
    ) {
      return false;
    }

    return true;
  });

  if (multilineReport) {
    filteredEvents.forEach(event => {
      if (event.teamWorkflowStatus) {
        event.teamWorkflowStatus = {
          ...event.teamWorkflowStatus,
          id: statusIdMap.get(event.teamWorkflowStatus.name)!,
        } as WorkflowStatus;
      }
    });
  }

  return filteredEvents;
}

function eventFragment(
  filters,
  page: number,
  controller: CycleTimeChartController,
  customFieldKeys: string[]
): Fragment {
  const paginatedFilters = { ...filters, page: page, per: EVENTS_PER_PAGE };

  return new Fragment().fragment(
    new Fragment('recordEvents')
      .argument('filters', paginatedFilters, 'RecordEventFilters!')
      .fragment(
        new Fragment('raw')
          .attr(
            'createdAt',
            'assignedToUserId',
            'eventType',
            'eventSubjectType',
            'teamId'
          )
          .fragment(
            ['Feature', 'Epic', 'Requirement'].reduce((fragment, type) => {
              let newFragment = new Fragment('', type)
                .union()
                .attr('id', 'name', 'referenceNum', 'path')
                .fragment(new Fragment('originalEstimate').attr('value'));

              // GraphQL requires args be present - only add them if they're not-nil
              if (controller.props.publishableId) {
                let drillInFragment = new Fragment('drillInUrl')
                  .argument('bookmarkId', controller.props.bookmarkId)
                  .argument('publishableId', controller.props.publishableId)
                  .argument(
                    'publishableType',
                    controller.props.publishableType
                  );

                if (controller.props.viewSnapshotId) {
                  drillInFragment = drillInFragment.argument(
                    'viewSnapshotId',
                    controller.props.viewSnapshotId
                  );
                }

                if (controller.props.dashboardId) {
                  drillInFragment = drillInFragment.argument(
                    'dashboardId',
                    controller.props.dashboardId
                  );
                }

                if (controller.props.exportType) {
                  drillInFragment = drillInFragment.argument(
                    'exportType',
                    controller.props.exportType
                  );
                }

                newFragment = newFragment.fragment(drillInFragment);
              }

              if (type === 'Feature') {
                // Fetch the "Feature Type", i.e. new, bug etc when displaying features
                newFragment = newFragment.fragment(
                  new Fragment('workflowKind').attr('id', 'name')
                );

                // Fetch the specified custom fields when displaying features
                if (customFieldKeys?.length) {
                  newFragment = newFragment.fragment(
                    new Fragment('customFieldValues')
                      .attr('id', 'value', 'humanValue', 'key')
                      .argument('keys', customFieldKeys, '[String!]')
                  );
                }
              }

              return fragment.fragment(
                newFragment.fragment(new Fragment('tags').attr('id', 'name'))
              );
            }, new Fragment('eventSubject'))
          )
          .fragment(
            new Fragment('teamWorkflowStatus').attr(
              'internalMeaning',
              'id',
              'name',
              'position'
            )
          )
      )
  );
}

async function fetchEventPage(
  filters: object,
  page: number,
  controller: CycleTimeChartController,
  customFieldKeys: string[]
): Promise<{ raw: RecordEventRaw[]; rawPageCount: number }> {
  const { recordEvents } = await ApplicationModel.query<{
    recordEvents: RecordEvent;
  }>(eventFragment(filters, page, controller, customFieldKeys));

  return {
    raw: recordEvents.raw,
    rawPageCount: orZero(recordEvents.rawPageCount),
  };
}

// Loads raw events with the given filters, mimicking the batching behaviour in recordEvents.ts,
// but with a simpler fragment and no association injection.
async function loadEventsInBatches({
  projectId,
  startDate,
  endDate,
  statusIds,
  customFieldKeys,
  teamIds,
  controller,
}: EventBatchParameters): Promise<RecordEventRaw[]> {
  let events: RecordEventRaw[] = [];
  let page = 1;
  let pagesLeft: number | null = null;

  // Don't load epic events if epics are disabled
  const eventSubjectType = !controller.state.bookmark.local.epicsEnabled
    ? undefined
    : ['Feature', 'Requirement'];

  const filters: any = {
    eventSubjectType,
    eventType: [
      aha.enums.RecordEventTypeEnum.RECORD_ADDED_TO_TEAM_WORKFLOW_STATUS,
    ],
    createdAt: {
      gt: startDate,
      lt: endDate,
    },
  };

  if (controller.state.bookmark.isMultilineReport) {
    filters.teamIds = teamIds;
  } else {
    filters.teamId = projectId;
    filters.teamWorkflowStatusId = statusIds;
  }

  while (pagesLeft === null || pagesLeft > 0) {
    const pagesToFetch =
      pagesLeft === null
        ? INITIAL_CONCURRENT_PAGES
        : Math.min(MAX_CONCURRENT_PAGES, pagesLeft);
    const results = await Promise.all(
      Array.from({ length: pagesToFetch }, (_, i) =>
        fetchEventPage(filters, page + i, controller, customFieldKeys ?? [])
      )
    );
    events = events.concat(...results.map(r => r.raw));
    if (pagesLeft === null) {
      pagesLeft = results[0].rawPageCount - 2;
    } else {
      pagesLeft -= pagesToFetch;
    }
    page += pagesToFetch;
  }

  return events;
}
